import Layout from "@/components/elements/Layout";

export default function PrivacyPolicy() {
  return (
    <Layout title={"Privacy Policy"}>
      <div className='px-4 py-8 mx-auto prose lg:py-16'>
        <h1 className='mb-0 text-4xl font-bold leading-tight text-white lg:text-6xl font-display'>
          Privacy Policy
        </h1>
        <p className='text-sm text-white lg:text-base text-opacity-80'>
          Effective Date: July 1st, 2022
        </p>
        <div className='max-w-screen-xl pt-4 lg:py-8'>
          <article className='prose prose-invert pb-8 prose-img:rounded-[10px] prose-td:p-2 max-w-5xl'>
            <p>
              At Lucidity Insights, we know you care about how your personal
              information is used and shared, and we take your privacy
              seriously. Please read the following to learn more about our
              Privacy Policy.
            </p>
            <p>
              <strong>
                By using or accessing the service in any manner, you acknowledge
                that you accept the practices and policies outlined in this
                Privacy Policy, and you hereby consent that we will collect, use
                and share your information in the following ways.
              </strong>
            </p>
            <p>
              Remember that your use of Lucidity Insight’s Services is at all
              times subject to our <strong>Terms of Service</strong>, which
              incorporates this Privacy Policy. Any terms we use in this Privacy
              Policy without defining them have the definitions given to them in
              the Terms of Service. Our Privacy Policy applies to all users of
              the Lucidity Insights website:{" "}
              <a
                href='https://lucidityinsights.com/'
                target='_blank'
                rel='noopener noreferrer'
              >
                www.lucidityinsights.com
              </a>{" "}
              (and any subdomains).
            </p>
            <p>
              Notwithstanding the foregoing or anything to the contrary in this
              Privacy Policy or the Terms of Service, Lucidity Insights’ use and
              transfer to any other app of information received from Google APIs
              will adhere to the {" "}
              <a
                href='https://developers.google.com/terms/api-services-user-data-policy'
                target='_blank'
                rel='noopener noreferrer'
              >
                Google API Services User Data Policy
              </a>{" "}
              , including the Limited Use requirements.
            </p>
            <h3>What does this Privacy Policy Cover?</h3>
            <p>
              This Privacy Policy covers how we treat Personal Data that we
              gather when you access or use our Service. “Personal Data” means
              any information that identifies or relates to a particular
              individual and also includes information referred to as
              “personally identifiable information” or “personal information”
              under applicable data privacy laws, rules, or regulations. This
              Privacy Policy does not cover the practices of companies we don’t
              own or control or people we don’t manage.
            </p>
            <h3>Changes to this Privacy Policy</h3>
            <p>
              Lucidity Insights may change this Privacy Policy from time to
              time. Laws, regulations, and industry standards evolve, which may
              make those changes necessary, or we may make changes to our
              Service or business.  We will post the changes to this page and
              encourage you to review our Privacy Policy to stay informed. If we
              make changes that materially alter your privacy rights, we will do
              our best to alert you to changes by placing a notice on the
              Lucidity Insights website, by sending you an email, and/or by some
              other means. Please note that if you’ve opted not to receive legal
              notice emails from us (or you haven’t provided us with your email
              address), those legal notices will still govern your use of the
              Service, and you are still responsible for reading and
              understanding them. 
            </p>
            <p>
              If you use the Service after any changes to the Privacy Policy
              have been posted, that means you agree to all of the changes. Use
              of information we collect is subject to the Privacy Policy in
              effect at the time such information is collected.
            </p>
            <h3>What Personal Data does Lucidity Insights Collect?</h3>
            <p>We collect Personal Data about you from:</p>
            <ul>
              <li>
                You:
                <ul className='list-[circle]'>
                  <li>When you provide such information directly to us, and</li>
                  <li>
                    When Personal Data about you is automatically collected in
                    connection with your use of our Service.
                  </li>
                </ul>
              </li>
              <li>
                Third parties, both when they provide us with Personal Data
                about you and when we seek out information for Profiles, as
                discussed below (“Third Parties”). Third Parties that share your
                Personal Data with us include:
                <ul className='list-[circle]'>
                  <li>
                    Service providers. For example, we may use analytics service
                    providers to analyze how you interact and engage with the
                    Service, or third parties may help us provide you with
                    customer support.
                  </li>
                  <li>
                    Other networks connected to the Service. If you provide any
                    third-party account credentials to us or otherwise sign-in
                    to the Service through a third-party site or service (for
                    example, using LinkedIn or Salesforce), you understand some
                    content and/or information in those accounts may be
                    transmitted into your Account with us.
                  </li>
                  <li>
                    Advertising partners. We receive information about you from
                    some of our service providers who assist us with marketing
                    or promotional services related to how you interact with our
                    websites, applications, products, services, advertisements,
                    or communications.
                  </li>
                </ul>
              </li>
            </ul>
            <p>
              Lucidity Insights also creates profiles of people and companies,
              which we call “Profiles,” from different sources. Once we have
              collected information (primarily business-related information)
              about a person or company, we combine multiple mentions of the
              same person or company into a Profile. Profiles are then made
              available to users of the Service, and our customers and strategic
              partners.
            </p>
            <p>
              When we create or enhance Profiles about individuals, we may
              collect this information from multiple sources, including:
            </p>
            <ul>
              <li>
                Publicly-available web sources that we scan using technology or
                manual methods.
              </li>
              <li>
                User contributions about themselves or other people and
                companies.
              </li>
              <li>Research conducted internally by Lucidity Insights.</li>
              <li>
                Other companies and data partners that license information to
                us.
              </li>
            </ul>
            <p>We collect information you provide directly to us through:</p>
            <ul>
              <li>Online registration</li>
              <li>Online forms</li>
              <li>Newsletter sign-up</li>
              <li>Requests or submissions via email</li>
              <li>Participation in surveys</li>
              <li>Reviews you submit about the service</li>
              <li>Uploads or posts to the service</li>
              <li>Requests for customer support</li>
            </ul>
            <p>We also collect information through automated means:</p>
            <p>
              When you use our website, we collect and analyze information such
              as your IP address, browser types, browser language, operating
              system, software and hardware attributes (including device IDs),
              referring and exit pages and URLs, the number of clicks, pages
              viewed and the order of those pages, date and time of use, content
              watched, total minutes watched, error logs, and other similar
              information about how you use the website.
            </p>
            <p>
              When you use our website, we collect and analyze information such
              as your IP address, browser types, browser language, operating
              system, software and hardware attributes (including device IDs),
              referring and exit pages and URLs, the number of clicks, pages
              viewed and the order of those pages, date and time of use, content
              watched, total minutes watched, error logs, and other similar
              information about how you use the website.
            </p>
            <p>
              When you use the Lucidity Insights service, we and our service
              providers automatically collect general location information
              (e.g., IP address, city/country and or postal code associated with
              an IP address, city/country from your computer).
            </p>
            <p>
              If you access the website through a third-party log-in (social
              networks such as Instagram, Facebook, Twitter etc.) you may allow
              us to collect and store information from your profile depending on
              your settings. This can include your name, gender, profile
              picture, “likes”, and friends lists. If you do not wish to provide
              us with this information, do not access the website through social
              networks. You may also have the option to share website activities
              on social networks. Your friends, followers, and subscribers will
              be able to view your activity on social networking services you
              enabled.
            </p>
            <h3>What Categories of Personal Data We Collect</h3>
            <p>
              The following chart details the categories of Personal Data that
              we collect and have collected over the past twelve (12) months.
              Throughout this Privacy Policy, we will refer back to the
              categories of Personal Data listed in this chart (for example,
              “Category A. Personal identifiers”).
            </p>
            <div className='overflow-x-auto'>
              <table className='min-w-[400px] table-auto w-full'>
                <thead>
                  <tr className='bg-white text-dark-2'>
                    <td></td>
                    <td className='font-bold'>Category of Personal data</td>
                    <td className='font-bold'>Examples</td>
                    <td className='w-1/4 font-bold'>Source of Data</td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className='font-bold'>A.</td>
                    <td className='font-bold'>Personal Identifiers</td>
                    <td>
                      Name, email, IP, username, social media profile IDs/links,
                      personal website; profile data: name, social profile
                      links, personal website
                    </td>
                    <td>You, Third Parties</td>
                  </tr>
                  <tr>
                    <td className='font-bold'>B.</td>
                    <td className='font-bold'>Customer Records</td>
                    <td>
                      Name, address, credit card type, last 4 digits of credit
                      card
                    </td>
                    <td>You</td>
                  </tr>
                  <tr>
                    <td className='font-bold'>C.</td>
                    <td className='font-bold'>
                      Protected classification characteristics
                    </td>
                    <td>Gender, Race, Citizenship (optional profile data)</td>
                    <td>You, Third Parties</td>
                  </tr>
                  <tr>
                    <td className='font-bold'>D.</td>
                    <td className='font-bold'>
                      Internet or other similar network activity information
                    </td>
                    <td>
                      Website &amp; application usage, interaction with
                      advertisements, browsers, platforms, device used, and
                      search history
                    </td>
                    <td>You, Third Parties</td>
                  </tr>
                  <tr>
                    <td className='font-bold'>E.</td>
                    <td className='font-bold'>Geolocation data</td>
                    <td>IP, location data (city, country)</td>
                    <td>Third Parties</td>
                  </tr>
                  <tr>
                    <td className='font-bold'>F.</td>
                    <td className='font-bold'>
                      Professional or employment- related information
                    </td>
                    <td>
                      Employment history, use case, role, industry; Profile
                      data: current &amp; past company, current and past job
                      titles, biography description, press references,
                      professional investments
                    </td>
                    <td>You, Third Parties</td>
                  </tr>
                  <tr>
                    <td className='font-bold'>G.</td>
                    <td className='font-bold'>
                      Personal Data a user chooses to enter into a free text
                      field within the Service
                    </td>
                    <td>
                      Any information that either directly or indirectly
                      identifies, relates to or describes a particular consumer
                      or household and/or is reasonably capable of being
                      associated with or could reasonably be linked to a
                      particular consumer or household.
                    </td>
                    <td>You, Third Parties</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              The following sections provide additional information about how we
              collect your Personal Data.
            </p>
            <p>
              <strong>Information You Provide to Us: </strong>We receive and
              store any information you knowingly provide to us. For example,
              through the registration process and/or through your account
              settings, we may collect Personal Data such as your name, email
              address, phone number, and third-party account credentials (for
              example, your log-in credentials for LinkedIn, e-mail, Salesforce
              or other third-party sites). If you provide your third-party
              account credentials to us or otherwise sign in to the Service
              through a third party site or service (such as Salesforce,
              Twitter, Facebook, Linkedin or Google), you understand some
              content and/or information in those accounts (“Third Party Account
              Information”) may be transmitted into your account with us, and
              that Third Party Account Information transmitted to our Service is
              covered by this Privacy Policy. Certain information may be
              required to register with us or to take advantage of some of our
              features.
            </p>
            <p>
              We may communicate with you if you’ve provided us the means to do
              so. For example, if you’ve given us your email address, we may
              send you promotional email offers on behalf of other businesses,
              or email you about your use of the Service. Also, we may receive a
              confirmation when you open an email from us, which helps us
              improve our Service. If you do not want to receive communications
              from us, please indicate your preference on the{" "}
              <strong>Profile Page</strong>.
            </p>
            <p>
              <strong>Information Collected Automatically: </strong>
              Like many other commercial websites, we may also gather
              information in connection with your use of the Site or Service
              through the use of cookies and other similar technologies such as
              server logs, pixel tags, web beacons, user website activity and
              JavaScript (collectively, “Cookies”) to enable us to recognize
              your web browser or device and tell us how and when you visit and
              use our Service, to analyze trends, to learn about our user base,
              and to operate and improve our Service. Cookies are small pieces
              of data–usually text files–placed on your computer, tablet, phone,
              or similar device when you use that device to visit our Service.
              We may also supplement the information we collect from you with
              information received from third parties, including third parties
              that have placed their own Cookies on your device(s). Please note
              that because of our use of Cookies, the Service does not support
              “Do Not Track” requests sent from a browser at this time.
            </p>
            <p>
              We use the following types of Cookies:
              <ul>
                <li>
                  Essential Cookies. Essential Cookies are required for
                  providing you with features or services that you have
                  requested. For example, certain Cookies enable you to log into
                  secure areas of our Service. Disabling these Cookies may make
                  certain features and services unavailable.
                </li>
                <li>
                  Functional Cookies. Functional Cookies are used to record your
                  choices and settings regarding our Service, maintain your
                  preferences over time and recognize you when you return to our
                  Service. These Cookies help us to personalize our content for
                  you, greet you by name, and remember your preferences (for
                  example, your choice of language or region).
                </li>
                <li>
                  Performance/Analytical Cookies. Performance/Analytical Cookies
                  allow us to understand how visitors use our Service such as by
                  collecting information about the number of visitors to the
                  Service, what pages visitors view on our Service and how long
                  visitors are viewing pages on the Service.
                  Performance/Analytical Cookies also help us measure the
                  performance of our advertising campaigns in order to help us
                  improve our campaigns and the Service’s content for those who
                  engage with our advertising. For example, Google, Inc.
                  (“Google”) uses cookies in connection with its Google
                  Analytics services. Google’s ability to use and share
                  information collected by Google Analytics about your visits to
                  the Service is subject to the Google Analytics Terms of
                  Service and the Google Privacy Policy. You have the option to
                  opt-out of Google’s use of cookies by visiting the Google
                  advertising opt-out page at{" "}
                  <a
                    href='https://policies.google.com/technologies/ads'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    https://policies.google.com/technologies/ads
                  </a>{" "}
                  or the Google Analytics Opt-out Browser Add-on at{" "}
                  <a
                    href='https://tools.google.com/dlpage/gaoptout/'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    https://tools.google.com/dlpage/gaoptout/
                  </a>
                  .
                </li>
                <li>
                  Retargeting/Advertising Cookies. Retargeting/Advertising
                  Cookies collect data about your online activity and identify
                  your interests so that we can provide advertising that we
                  believe is relevant to you. For more information about this,
                  please see the section below titled “Information about
                  Interest-Based Advertisements.”
                </li>
              </ul>
            </p>
            <p>
              You can decide whether or not to accept Cookies through your
              internet browser’s settings. Most browsers have an option for
              turning off the Cookie feature, which will prevent your browser
              from accepting new Cookies, as well as (depending on the
              sophistication of your browser software) allow you to decide on
              acceptance of each new Cookie in a variety of ways. You can also
              delete all Cookies that are already on your computer. If you do
              this, however, you may have to manually adjust some preferences
              every time you visit a site, and this may cause some services and
              functionalities to not work.
            </p>
            <p>
              To explore what Cookie settings are available to you, look in the
              “preferences” or “options” section of your browser’s menu. To find
              out more information about Cookies, including information about
              how to manage and delete Cookies, please visit
              <a
                href='http://www.allaboutcookies.org.'
                target='_blank'
                rel='noopener noreferrer'
              >
                http://www.allaboutcookies.org.
              </a>
            </p>
            <p>
              <strong>Information about Interest-Based Advertisements: </strong>
              We may serve advertisements, and also allow third-party ad
              networks, including third-party ad servers, ad agencies, ad
              technology vendors and research firms, to serve advertisements
              through the Service. These advertisements may be targeted to users
              who fit certain general profile categories or display certain
              preferences or behaviors (“Interest-Based Ads”). Information for
              Internet-Based Ads (including Personal Data) may be provided to us
              by you, or derived from the usage patterns of particular users on
              the Service and/or services of third-parties. Such information may
              be gathered through tracking users’ activities across time and
              unaffiliated properties, including when you leave the Service. To
              accomplish this, we or our service providers may deliver Cookies,
              including a file (known as a “web beacon”) from an ad network to
              you through the Service. Web beacons allow ad networks to provide
              anonymized, aggregated auditing, research and reporting for us and
              for advertisers. Web beacons also enable ad networks to serve
              targeted advertisements to you when you visit other websites. Web
              beacons allow ad networks to view, edit or set their own Cookies
              on your browser, just as if you had requested a web page from
              their site.
            </p>
            <p>
              Several media and marketing associations have developed an
              industry self-regulatory program to give consumers a better
              understanding of, and greater control over, ads that are
              customized based on a consumer’s online behavior across different
              websites and properties. To make choices about Interest-Based Ads
              from participating third parties, including to opt-out of
              receiving behaviorally targeted advertisements from participating
              organizations, please visit the Digital Advertising Alliance or
              Network Advertising Initiative consumer opt-out pages, which are
              located at
              <a
                href='http://www.networkadvertising.org/choices/'
                target='_blank'
                rel='noopener noreferrer'
              >
                http://www.networkadvertising.org/choices/
              </a>{" "}
              or{" "}
              <a
                href='http://www.aboutads.info/choices'
                target='_blank'
                rel='noopener noreferrer'
              >
                http://www.aboutads.info/choices
              </a>
              . Users in the European Union should visit the European
              Interactive Digital Advertising Alliance’s user information
              website at{" "}
              <a
                href=' https://www.youronlinechoices.eu/'
                target='_blank'
                rel='noopener noreferrer'
              >
                https://www.youronlinechoices.eu/
              </a>
              .
            </p>
            <h3>How We Use Your Personal Data</h3>
            <p>
              We process Personal Data to operate, improve, understand and
              personalize our Service. We use Personal Data for the following
              purposes:
              <ul>
                <li>
                  To complete a purchase, process orders or other transactions.
                </li>
                <li>
                  To provide you with better customer service and assistance to
                  use our Service
                </li>
                <li>
                  To meet or fulfill the reason you provided the information to
                  us.
                </li>
                <li>
                  To communicate with you about the Service, including Service
                  announcements and offers.
                </li>
                <li>
                  To create and manage your Account or other user profiles.
                </li>
                <li>
                  To personalize your experience, website content and
                  communications based on your preferences, including targeted
                  offers and ads served through the Service.
                </li>
                <li>To respond to user inquiries and fulfill user requests.</li>
                <li>
                  To market, improve, and develop the Service, including
                  testing, research, analysis, and product development
                  (including creation, enhancement, and distribution of
                  Profiles).
                </li>
                <li>
                  To protect against or deter fraudulent, illegal or harmful
                  actions and maintain the safety, security and integrity of our
                  Service.
                </li>
                <li>
                  To comply with our legal or contractual obligations, resolve
                  disputes, and enforce our Terms of Service.
                </li>
                <li>
                  For any other business purpose stated when collecting your
                  Personal Data or as otherwise set forth in applicable data
                  privacy laws, such as the California Consumer Privacy Act (the
                  “CCPA”).
                </li>
              </ul>
            </p>
            <p>
              We will not collect additional categories of Personal Data or use
              the Personal Data we collected for materially different,
              unrelated, or incompatible purposes without providing you notice.
            </p>
            <p>
              As noted in the list above, we may communicate with you if you’ve
              provided us the means to do so. For example, if you’ve given us
              your email address, we may send you promotional email offers on
              behalf of other businesses or email you about your use of the
              Service.  Also, we may receive a confirmation when you open an
              email from us, which helps us improve our Service. If you do not
              want to receive communications from us, please indicate your
              preference on the <strong>Account Information Page</strong>  or
              contact us at{" "}
              <a href='mailto:support@lucidityinsights.com'>
                support@lucidityinsights.com
              </a>
            </p>
            <h3>How We Share Your Personal Data</h3>
            <h3 className='italic'>
              Disclosures of Personal Data for a Business Purpose
            </h3>
            <p>
              We disclose your Personal Data to service providers and other
              parties for the following business purposes:
              <ul>
                <li>
                  Performing services on our behalf, including maintaining or
                  servicing accounts, providing customer service, processing or
                  fulfilling orders and transactions, verifying customer
                  information, processing payments, providing advertising or
                  marketing services, providing analytic services, or providing
                  similar services on behalf of the business or service
                  provider.
                </li>
                <li>
                  Auditing related to a current interaction and concurrent
                  transactions, including, but not limited to, counting ad
                  impressions to unique visitors, verifying positioning and
                  quality of ad impressions, and auditing compliance with this
                  specification and other standards.
                </li>
                <li>
                  Detecting security incidents, protecting against malicious,
                  deceptive, fraudulent, or illegal activity, and prosecuting
                  those responsible for that activity.
                </li>
                <li>
                  Debugging to identify and repair errors that impair existing
                  intended functionality.
                </li>
                <li>
                  Short-term, transient use of Personal Data that is not used by
                  another party to build a consumer profile or otherwise alter
                  your consumer experience outside the current interaction.
                </li>
                <li>
                  Undertaking internal research for technological development
                  and demonstration.
                </li>
                <li>
                  Undertaking activities to verify or maintain the quality or
                  safety of a service or device that we own, manufacture, was
                  manufactured for us, or we control.
                </li>
              </ul>
            </p>
            <p>
              We disclose your Personal Data to the following categories of
              service providers and other parties:
              <ul>
                <li>Service providers, including:</li>
                <li>
                  Subscription management providers and payment processors
                </li>
                <li>Security and fraud prevention consultants</li>
                <li>
                  Hosting and other technology and communications providers
                </li>
                <li>Analytics providers</li>
                <li>
                  Customer relationship management and customer support
                  providers
                </li>
                <li>Ad networks</li>
                <li>
                  Parties who acquire your Personal Data through an acquisition
                  or other change of control.
                  <ul className='list-[circle]'>
                    <li>
                      Personal Data may be transferred to a third party if we
                      undergo a merger, acquisition, bankruptcy or other
                      transaction in which that third party assumes control of
                      our business (in whole or in part)
                    </li>
                  </ul>
                </li>
                <li>
                  Other parties at your direction.
                  <ul className='list-[circle]'>
                    <li>
                      Other users (where you post information publicly or as
                      otherwise necessary to effect a transaction initiated or
                      authorized by you through the Service)
                    </li>
                    <li>
                      Social media services (if you intentionally interact with
                      them through your use of the Service)
                    </li>
                    <li>
                      Third-party business partners who you access through the
                      Service
                    </li>
                    <li>Other parties authorized by you</li>
                  </ul>
                </li>
                <li>
                  Business partners
                  <ul className='list-[circle]'>
                    <li>
                      In some cases, we may disclose information about your use
                      of the Service to your employer or other users within your
                      organization
                    </li>
                  </ul>
                </li>
                <li>A. Personal identifiers.</li>
                <li>B. Customer records identified by state law.</li>
                <li>
                  C. Protected classification characteristics under state or
                  federal law.
                </li>
                <li>
                  D. Internet or other similar network activity information.
                </li>
                <li>E. Geolocation data.</li>
                <li>F. Professional or employment-related information.</li>
              </ul>
            </p>
            <h3>Sales of Personal Data</h3>
            <p>
              At Lucidity Insights, we collect information from a variety of
              sources (including information contributed directly by you and/or
              our user community) in order to compile Profiles about entities
              and individuals. Please refer to the chart above titled
              “Categories of Personal Data We Collect” for a better
              understanding of the Personal Data we collect that constitutes
              Profile data.
            </p>
            <p>
              In this section, we use the term ‘sell’ as it is defined in the
              CCPA. We sell your Profile data to our customers and data partners
              (such as resellers and syndication partners), subject to your
              right to opt-out of these sales.
            </p>
            <p>
              We may also share your information with advertisers in order to
              help provide our ads to you. The CCPA considers this a “sale” and,
              because of this, we are required to disclose these types of
              transfers as “sales” of personal information. However, we do not
              sell this information for monetary consideration. This type of
              sharing is also subject to your right to opt out of these sales.
            </p>
            <p>
              We have not ‘sold’ any data as yet, but we imagine in the next
              twelve months, we may sell the following categories of your
              Personal Data to third parties:
              <ul>
                <li>A. Personal identifiers.</li>
                <li>
                  C. Protected classification characteristics under state or
                  federal law.
                </li>
                <li>F. Professional or employment-related information.</li>
              </ul>
            </p>
            <h3>Data Security and Retention</h3>
            <p>
              We seek to protect your Personal Data from unauthorized access,
              use and disclosure using appropriate physical, technical,
              organizational and administrative security measures based on the
              type of Personal Data and how we are processing that data,
              including but not limited to:
              <ul>
                <li>The Lucidity Insights website uses SSL (https).</li>
                <li>
                  Account passwords are hashed when stored in our database.
                </li>
                <li>
                  The authenticity of request methods are verified to prevent
                  CSRF (cross-site request forgery) attacks.
                </li>
                <li>
                  Lucidity Insights employees use Single Sign-On (SSO) and
                  passwords and enable screen locking.
                </li>
                <li>
                  Access to AWS and Payment Processors is limited on a need to
                  know basis and requires Two-Factor Authentication (2FA).
                </li>
                <li>
                  Access to production data requires VPN access which is
                  restricted to only the people that need it.
                </li>
                <li>
                  Lucidity Insights performs third-party penetration testing.
                </li>
              </ul>
            </p>
            <p>
              You should also help protect your data by appropriately selecting
              and protecting your password and/or other sign-on mechanism;
              limiting access to your computer or device and browser; and
              signing off after you have finished accessing your account.
              Although we work to protect the privacy of your account and other
              Personal Data that we hold in our records, no security system is
              impenetrable, and unanticipated system failures or the efforts of
              malicious actors are an unfortunate reality on the Internet.
              Therefore, Lucidity Insights cannot guarantee that Personal Data
              during transmission through the Internet or while stored on our
              systems or otherwise in our care will be absolutely safe from
              intrusion by others.
            </p>
            <p>
              We retain Personal Data about you for as long as you have an open
              account with us or as otherwise necessary to provide the Service.
              In some cases we retain Personal Data for longer, if doing so is
              necessary to comply with our legal obligations, resolve disputes
              or collect fees owed, or is otherwise permitted or required by
              applicable law, rule or regulation. Afterwards, we retain some
              information in a depersonalized or aggregated form but not in a
              way that would identify you personally.
            </p>
            <h3>Personal Data of Children</h3>
            <p>
              As noted in the Terms of Service, we do not knowingly collect or
              solicit Personal Data from children under 16. If you are a child
              under 16, please do not attempt to register for or otherwise use
              the Service or send us any Personal Data. If we learn we have
              collected Personal Data from a child under 16, we will delete that
              information as quickly as possible. If you believe that a child
              under 16 may have provided us Personal Data, please contact us at{" "}
              <a href='mailto:support@lucidityinsights.com'>
                support@lucidityinsights.com
              </a>
              .
            </p>
            <h3>European Union Data Subject Rights</h3>
            <p>
              If you are a resident of the European Union (“EU”), United
              Kingdom, Lichtenstein, Norway, or Iceland, you may have additional
              rights under the EU General Data Protection Regulation (the
              “GDPR”) with respect to your Personal Data, as outlined in
              our GDPR Privacy Policy.
            </p>
            <p>
              There are other jurisdictions that may have additional rights as
              well, such as California and Nevada States in the United States of
              America. We encourage you to explore and understand what your
              rights are under those jurisdictions with respect to your Personal
              Data.
            </p>
            <h3>Contact Information</h3>
            <p>
              If you have any questions or comments about this Privacy Policy,
              the ways in which we collect and use your Personal Data, your
              choices and rights regarding such use, please do not hesitate to
              contact your Lucidity Insights representative or email us at{" "}
              <a href='mailto:support@lucidityinsights.com'>
                {" "}
                support@lucidityinsights.com
              </a>
            </p>
          </article>
        </div>
      </div>
    </Layout>
  );
}
